import { gql } from '@apollo/client';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import useAppSelector from '../../app/redux/useAppSelector';
import SVGIcon from '../../components/SVGIcon';
import type { SharingDropdownAddSelect_PostFragment } from '../../graphql/generated/graphqlTypes';
import checkIcon from '../../icons/check.svg';
import InvitationModal from '../../invitation/InvitationModal';
import { inputToOptions } from '../../invitation/InvitationUserSelect';
import getSuggestedEmail from '../../invitation/getSuggestedEmail';
import type { EntitySelectConfig } from '../../search/entitySearch/CategorizedEntitySelect';
import CategorizedEntitySelect from '../../search/entitySearch/CategorizedEntitySelect';
import EntitySelect from '../../search/entitySearch/EntitySelect';
import topicPolicyCheck, {
  MEMBER_EDITABLE_NONE,
  TopicPolicyAction,
} from '../../topic/topicPolicyCheck';
import UIActions from '../../ui/UIActions';
import useAllUsers from '../../user/useAllUsers';
import { isFeatureEnabled } from '../../utils/features';
import searchHighlight from '../../utils/search/searchHighlight';
import { usePublicContentPermissions } from '../../utils/useAllowPublicContentBy';
import styles from './SharingDropdownAddSelect.module.scss';

const POST_FRAGMENT = gql`
  fragment SharingDropdownAddSelect_post on Post {
    id
    sharing {
      editorIds
      viewerIds
      editorGroupIds
      viewerGroupIds
    }
    topics {
      id
    }
    ...InvitationModal_initialPost
  }

  ${InvitationModal.fragments.initialPost}
`;

interface SharingDropdownAddSelectProps {
  onAddTopic: (topicId: string) => void;
  onAddUser: (userId: string) => void;
  onAddGroup: (groupId: string) => void;
  post: SharingDropdownAddSelect_PostFragment;
}

const SharingDropdownAddSelect = ({
  onAddTopic,
  onAddUser,
  onAddGroup,
  post,
}: SharingDropdownAddSelectProps) => {
  const dispatch = useDispatch();
  const organizationEmail = useAppSelector(
    (state) => state.session.organization.email,
  );
  const allUsers = useAllUsers();

  const userEmails = useMemo(
    () => new Set(allUsers.map((user) => user.email)),
    [allUsers],
  );

  const currentUser = useAppSelector((state) => state.session.user);
  const { isPermittedToPublicizePost } = usePublicContentPermissions();

  const { sharing, topics } = post;

  const selectedIdSet = useMemo(
    () => new Set(topics?.map((topic) => topic.id)),
    [topics],
  );

  const config = useMemo<EntitySelectConfig>(() => {
    // TODO: specify topic type
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getTopicExtra = (topic: any) => {
      let disabledMessage = null;
      if (topic.id && selectedIdSet.has(topic.id)) {
        disabledMessage = (
          <>
            Already added to the post{' '}
            <SVGIcon inline size="small">
              {checkIcon}
            </SVGIcon>
          </>
        );
      }
      if (!topicPolicyCheck(TopicPolicyAction.EditPost, topic, currentUser)) {
        disabledMessage =
          topic.memberEditable === MEMBER_EDITABLE_NONE
            ? 'Restricted to owners'
            : 'Restricted to members';
      } else if (!isPermittedToPublicizePost && topic.privacy === 'PUBLIC') {
        disabledMessage = 'Only admins can add posts to public topics';
      }

      return {
        isDisabled: !!disabledMessage,
        secondary: disabledMessage,
      };
    };

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const topicSortBy = (l: any, r: any) => {
      const { isDisabled: isLDisabled } = getTopicExtra(l);
      const { isDisabled: isRDisabled } = getTopicExtra(r);

      if (isLDisabled === isRDisabled) {
        return 0;
      }

      return isLDisabled ? 1 : -1;
    };

    const userIdSet = new Set([
      ...(sharing?.editorIds || []),
      ...(sharing?.viewerIds || []),
    ]);

    const groupIdSet = new Set([
      ...(sharing?.editorGroupIds || []),
      ...(sharing?.viewerGroupIds || []),
    ]);

    return {
      topic: { sortBy: topicSortBy, extra: getTopicExtra },
      user: {
        extra: (user: { id: string }) => {
          const isDisabled = userIdSet.has(user.id);

          return {
            isDisabled,
            secondary: isDisabled ? 'Already added to the post' : null,
          };
        },
      },
      group: {
        extra: (group: { id: string }) => {
          const isDisabled = groupIdSet.has(group.id);

          return {
            isDisabled,
            secondary: isDisabled ? 'Already added to the post' : null,
          };
        },
      },
      order: ['user', 'group', 'topic'],
    };
  }, [currentUser, isPermittedToPublicizePost, selectedIdSet, sharing]);

  if (isFeatureEnabled('categorized_entity_select')) {
    return (
      <CategorizedEntitySelect
        className={classNames(styles.select, styles.categorizedSelect)}
        controlShouldRenderValue={false}
        onChange={(value) => {
          if (!value) return;
          const { type, id } = value;
          if (type === 'user') onAddUser(id);
          if (type === 'topic') onAddTopic(id);
          if (type === 'group') onAddGroup(id);
        }}
        placeholder="Add topics, users, or email invite"
        config={config}
        value={null}
        getNewGroup={(inputValue) => {
          const suggestedEmail = getSuggestedEmail(
            inputValue,
            organizationEmail,
          );
          if (
            (inputValue && !suggestedEmail) ||
            userEmails.has(suggestedEmail)
          ) {
            return null;
          }
          return {
            label: 'Email invite',
            options: [
              {
                label: inputValue
                  ? searchHighlight(suggestedEmail, inputValue)
                  : `teammate@${organizationEmail}`,
                name: inputValue,
                value: inputValue,
                secondary: 'Invite new member with email',
                onSelect: () => {
                  dispatch(
                    UIActions.openModal(InvitationModal, {
                      disableOutsideClose: true,
                      initialOptions: suggestedEmail
                        ? inputToOptions(suggestedEmail)
                        : undefined,
                      initialPost: post,
                    }),
                  );
                },
              },
            ],
          };
        }}
      />
    );
  }

  return (
    <EntitySelect
      className={styles.select}
      controlShouldRenderValue={false}
      isMulti
      onChange={(value) => {
        if (!value[0]) return;
        const { type, id } = value[0];
        if (type === 'user') onAddUser(id);
        if (type === 'topic') onAddTopic(id);
        if (type === 'group') onAddGroup(id);
      }}
      placeholder="Add topics, users, or email invite"
      config={config}
      value={[]}
      creatable
      onCreateOption={(input: string) => {
        dispatch(
          UIActions.openModal(InvitationModal, {
            disableOutsideClose: true,
            initialOptions: inputToOptions(
              getSuggestedEmail(input, organizationEmail),
            ),
            initialPost: post,
          }),
        );
      }}
      isValidNewOption={(input: string) => {
        const suggestedEmail = getSuggestedEmail(input, organizationEmail);
        return (
          !userEmails.has(suggestedEmail) && !!inputToOptions(suggestedEmail)
        );
      }}
      noOptionsMessage={() => 'Enter a valid email to invite…'}
      getNewOptionData={(inputValue: string) => ({
        id: 'new',
        label: searchHighlight(
          getSuggestedEmail(inputValue, organizationEmail),
          inputValue,
        ),
        name: inputValue,
        value: inputValue,
        secondary: 'Invite new member with email',
        type: undefined,
        __isNew__: true,
      })}
    />
  );
};

SharingDropdownAddSelect.fragments = {
  post: POST_FRAGMENT,
};

export default SharingDropdownAddSelect;
